import { FieldError, FieldErrors, FormProvider, Path, useFieldArray, useForm } from 'react-hook-form';
import { Appointment, BulkAssignListForm, EExceptionType, Invoice, MeterPointDetails } from '../../types';
import { EHttpMethod, usePortalQuery } from '../../hooks/usePortalQuery';
import { useState, useMemo, useEffect } from 'react';
import { FormButton } from '@portal_components/components/FormButton';
import axios from 'axios';
import { EnvironmentVariables } from '../../EnvironmentVariables';
import { BulkAssignRecord } from './BulkAssignRecord';
import { useBulkAssignYupResolver } from '../../hooks/useBulkAssignYupResolver';
import { useNavigate } from 'react-router-dom';
import { Button } from '@portal_components/components/Button';
import { DateTime } from 'luxon';
import { useBulkAssignFormValidationSchema } from '../../hooks/useValidationSchema';

interface BulkAssignListProps {
  siteList: MeterPointDetails[];
  customerGroupId: string;
  contractId: string;
  appointments: { [mprn: string]: Appointment };
  handoverFileName: string;
  handleAppointmentRemove: (mprn: string) => void;
  handleRefreshAppointments: () => void;
}

export const BulkAssignForm = ({
  siteList,
  customerGroupId,
  contractId,
  appointments,
  handoverFileName,
  handleAppointmentRemove,
  handleRefreshAppointments
}: BulkAssignListProps) => {
  const navigate = useNavigate();
  const validationSchema = useBulkAssignFormValidationSchema();
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [submitResponse, setSubmitResponse] = useState<{
    success: boolean;
    message: string;
    hasExceptions?: boolean;
  } | null>(null);
  const [expandedRecordIndex, setExpandedRecordIndex] = useState<number | null>(null);

  const { data: invoices, isLoading: invoicesLoading } = usePortalQuery<Invoice[]>(
    EHttpMethod.GET,
    `invoices/${customerGroupId}`,
    `invoices/${customerGroupId}`,
    !!customerGroupId,
    undefined,
    undefined,
    undefined,
    undefined,
    600000,
    600000
  );

  const defaultInvoiceGroup = useMemo(() => {
    if (!invoices || invoices.length === 0) return undefined;
    const contractInvoiceGroup = invoices.find(invoice => invoice.contractId == contractId);
    if (contractInvoiceGroup) {
      return contractInvoiceGroup.invoiceGroupId;
    }
    return invoices.find(invoice => invoice.defaultInvoiceGroup)?.invoiceGroupId;
  }, [contractId, invoices]);

  const appointmentsPresentWithDuplicatesOrExceptions = useMemo(() => {
    return appointments
      ? Object.values(appointments).some(
          appointment => appointment.duplicate || appointment.hasOpenExceptions
        )
      : false;
  }, [appointments]);

  const methods = useForm<BulkAssignListForm>({
    defaultValues: { siteList },
    resolver: useBulkAssignYupResolver(validationSchema, appointments)
  });

  const formSiteList = methods.watch('siteList');

  useEffect(() => {
    // Remove appointments that are no longer in the site list
    const siteListMpans: string[] = formSiteList.reduce((acc: string[], site) => {
      if (site.mprn) {
        acc.push(site.mprn);
      }
      return acc;
    }, []);
    for (const mpan of Object.keys(appointments)) {
      if (!siteListMpans.includes(mpan)) {
        handleAppointmentRemove(mpan);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments, handleAppointmentRemove]);

  useEffect(() => {
    if (Object.keys(appointments).length === 0) return;

    const siteList = methods.getValues('siteList');
    if (!siteList || siteList.length === 0) return;

    for (let siteIndex = 0; siteIndex < siteList.length; siteIndex++) {
      const site = siteList[siteIndex];
      if (!site.mprn) continue;

      if (defaultInvoiceGroup) {
        methods.setValue(`siteList.${siteIndex}.invoiceGroupId`, defaultInvoiceGroup);
      }

      const appointment = appointments[site.mprn];
      if (!appointment) continue;

      methods.setValue(`siteList.${siteIndex}.contractSiteStatusId`, appointment.active ? '10' : '5');
      if (appointment.active && appointment.effectiveDate) {
        const currentEffectiveDate = methods.getValues(`siteList.${siteIndex}.effectiveDate`);
        if (!currentEffectiveDate) continue; // shouldn't be possible
        const effectiveDateTime = DateTime.fromISO(currentEffectiveDate);
        const appointmentDateTime = DateTime.fromISO(appointment.effectiveDate);
        if (appointmentDateTime.isValid && appointmentDateTime > effectiveDateTime) {
          methods.setValue(`siteList.${siteIndex}.effectiveDate`, appointment.effectiveDate);
        }
      }
    }
  }, [appointments, defaultInvoiceGroup, methods]);

  const { fields, remove } = useFieldArray<BulkAssignListForm, `siteList`>({
    control: methods.control,
    name: `siteList`
  });

  const handleRemoveAndUpdateOverAllFileStatus = (index: number, removed_mprn: string) => {
    remove(index);
    // make sure there isn't a duplicate MPAN before deleting the appointment
    const hasDuplicateMpan = formSiteList.filter(site => site.mprn == removed_mprn).length > 1;
    if (!hasDuplicateMpan) {
      handleAppointmentRemove(removed_mprn);
    }
  };

  const handleSubmit = async (
    values: BulkAssignListForm & {
      invalidSiteList?: { siteDetails: MeterPointDetails; errorDescription: string }[];
    }
  ) => {
    setFormSubmitted(true);
    setSubmitResponse(null);

    const { siteList, invalidSiteList } = values;
    // only try to assign sites if there are more than 0 valid ones
    if (siteList.length > 0) {
      const response = await axios
        .post(
          `${EnvironmentVariables.ApiUrl}/assign-mpan/bulk`,
          { siteList, contractId, customerGroupId, handoverFileName },
          {
            headers: {
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        )
        .catch(error => error.response);
      if (!response) {
        setSubmitResponse({ success: false, message: 'An error occurred while assigning MPANs.' });
        setFormSubmitted(false);
        return;
      } else if (response.status !== 200) {
        setSubmitResponse({ success: false, message: response.data });
        setFormSubmitted(false);
        return;
      }
    }

    if (invalidSiteList && invalidSiteList.length > 0) {
      const invalidSitesResponse = await axios
        .post(
          `${EnvironmentVariables.ApiUrl}/exceptions`,
          {
            exceptions: invalidSiteList,
            contractId,
            customerGroupId,
            handoverFileName,
            exceptionType: EExceptionType.VALIDATION_EXCEPTION
          },
          {
            headers: {
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        )
        .catch(error => error.response);
      if (!invalidSitesResponse) {
        setSubmitResponse({ success: false, message: 'An error occurred while creating exceptions.' });
        setFormSubmitted(false);
        return;
      } else if (invalidSitesResponse.status !== 200) {
        setSubmitResponse({ success: false, message: invalidSitesResponse.data });
        setFormSubmitted(false);
        return;
      }
    }
    setSubmitResponse({
      success: true,
      message: `Successfully assigned ${siteList.length} MPANs and created ${invalidSiteList ? invalidSiteList.length : 0} exceptions.`,
      hasExceptions: invalidSiteList && invalidSiteList.length > 0
    });
    methods.reset({ siteList: [] });
  };

  const onSubmitWithErrors = (errors: FieldErrors<BulkAssignListForm>) => {
    for (const [name, error] of Object.entries(errors)) {
      methods.control.setError(name as Path<BulkAssignListForm>, error as FieldError);
    }
  };

  if (invoicesLoading) return <div className="flex flex-row justify-center">Loading invoices...</div>;

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={!formSubmitted ? methods.handleSubmit(handleSubmit, onSubmitWithErrors) : undefined}>
          <div className="w-full flex flex-col justify-start mb-4 gap-2">
            <div className="min-h-6">
              {submitResponse && (
                <>
                  <div className={`${submitResponse.success ? 'text-feedback-green' : 'text-feedback-red'}`}>
                    {submitResponse.message}
                  </div>
                  {submitResponse.hasExceptions && (
                    <p
                      className="text-interactive-coral-db-70 hover:text-interactive-coral-db-50 cursor-pointer"
                      onClick={() => navigate(`/exceptions/${handoverFileName}`)}
                    >
                      {'Click here to view exceptions'}
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="flex flex-row gap-2">
              <FormButton
                type="submit"
                disabled={formSubmitted || appointmentsPresentWithDuplicatesOrExceptions}
                primary
              >
                Submit Handover
              </FormButton>
              <Button
                text="Refresh"
                primary={false}
                disabled={formSubmitted}
                onClick={handleRefreshAppointments}
              />
            </div>
          </div>
          {methods.formState.errors.siteList && (
            <p className="text-feedback-red mb-4">{methods.formState.errors.siteList.message}</p>
          )}
          {appointmentsPresentWithDuplicatesOrExceptions && (
            <div className="text-feedback-red mb-4">
              <p>
                There are MPANs below with which are already in exception or are duplicates against the
                selected Customer Contract. Where the MPAN is in exception, please refer to the exception page
                to resolve it. If the MPAN is marked as a duplicate, the MPAN can be updated with new details
                via the Edit function.
              </p>
            </div>
          )}
          <div className="pt-6 pl-2">
            {fields.length > 0 &&
              fields.map((siteDetails, index) => (
                <BulkAssignRecord
                  key={`${siteDetails.mprn}-${index}`}
                  details={siteDetails}
                  index={index}
                  expandedRecordIndex={expandedRecordIndex}
                  setExpandedRecordIndex={setExpandedRecordIndex}
                  handleRemoveRecord={handleRemoveAndUpdateOverAllFileStatus}
                  invoices={invoices ?? []}
                  appointmentActive={!!siteDetails.mprn && !!appointments?.[siteDetails.mprn]?.active}
                  appointmentDuplicate={!!siteDetails.mprn && !!appointments?.[siteDetails.mprn]?.duplicate}
                  hasOpenExceptions={
                    !!siteDetails.mprn && !!appointments?.[siteDetails.mprn]?.hasOpenExceptions
                  }
                />
              ))}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
